import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';
import type { CargoProblemWorkspaceMessageItem } from '@magimp/shared';
import { useMarkCargoProblemRead } from './queries.js';

const AUTO_READ_DEBOUNCE_MS = 400;

interface ReadTrackingParams {
  problemId: string;
  scrollRef: RefObject<HTMLElement | null>;
  messages: readonly CargoProblemWorkspaceMessageItem[];
  meId: string | null;
  /** False when nothing is unread — an already-read conversation posts nothing. */
  enabled: boolean;
}

/**
 * @aiPurpose personal read state driven by what the reader actually saw.
 * @aiInvariant only messages from OTHER participants advance the pointer, and
 *   only while the tab is visible: a backgrounded tab can still fire layout-tied
 *   intersections, and marking those read would silently drop the unread signal.
 * @aiInvariant the pointer only ever moves forward — scrolling back up through
 *   history cannot rewind it.
 * @aiInvariant "forward" is a position in the merged conversation, never a
 *   timestamp: two messages written inside one millisecond share a `createdAt`,
 *   so a timestamp comparison would refuse to advance from the first to the
 *   second. Positions shift when an older page is prepended, so the sent
 *   message is remembered by id and re-located in the current order.
 */
export function useCargoProblemReadTracking({
  problemId,
  scrollRef,
  messages,
  meId,
  enabled,
}: ReadTrackingParams): void {
  const markRead = useMarkCargoProblemRead(problemId);
  const pendingRef = useRef<string | null>(null);
  const sentRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const meta = useMemo(() => {
    const index = new Map<string, { authorId: string; position: number }>();
    messages.forEach((item, position) => {
      index.set(item.id, { authorId: item.author.id, position });
    });
    return index;
  }, [messages]);

  const isAhead = useCallback(
    (candidate: string, reference: string | null) => {
      if (reference === null) return true;
      const ahead = meta.get(candidate)?.position;
      const behind = meta.get(reference)?.position;
      if (ahead === undefined || behind === undefined) return false;
      return ahead > behind;
    },
    [meta],
  );

  const flush = useCallback(() => {
    timerRef.current = null;
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (!isAhead(pending, sentRef.current)) return;
    sentRef.current = pending;
    markRead.mutate(pending);
  }, [isAhead, markRead]);

  const schedule = useCallback(
    (id: string) => {
      if (isAhead(id, pendingRef.current)) pendingRef.current = id;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, AUTO_READ_DEBOUNCE_MS);
    },
    [flush, isAhead],
  );

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (!enabled) return;
    const root = scrollRef.current;
    if (!root) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = (entry.target as HTMLElement).dataset['cargoProblemMessageId'];
          if (!id) continue;
          const found = meta.get(id);
          if (!found || found.authorId === meId) continue;
          schedule(id);
        }
      },
      { root, threshold: 0.5 },
    );
    root
      .querySelectorAll<HTMLElement>('[data-cargo-problem-message-id]')
      .forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [enabled, meId, meta, schedule, scrollRef]);
}
